import React, { useEffect, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import styled from 'styled-components';
import { Button, GridContainer, emperor, white } from '@parchment/atoms';
import ComposedTextField from '../ComposedField/ComposedTextField';
import { renderToStaticMarkup } from 'react-dom/server';
import { Checkbox } from '../Checkbox/Checkbox';
const INITIAL_STATE = {
  value: '',
  firstName: '',
  middleName: '',
  lastName: '',
  checked: false,
  valid: {
    firstName: false,
    middleName: true,
    lastName: false,
    checked: false
  }
};
const CONSENT_TEXT = 'I consent to the disclosure of the credentials and any provided attachments to the delivery recipient, each as I\'ve selected above, and for the purpose identified by me above.';
const Signature = props => {
  const [prevSignature, setPrevSignature] = useState(props.value ? atob(props.value) : '');
  const [formData, setFormData] = useState({
    ...INITIAL_STATE,
    value: props.value || ''
  });
  const [formReady, setFormReady] = useState(false);
  const canvasRef = useRef(null);
  useEffect(() => {
    var _canvasRef$current;
    prevSignature && ((_canvasRef$current = canvasRef.current) === null || _canvasRef$current === void 0 ? void 0 : _canvasRef$current.fromDataURL(prevSignature));
  }, []);
  useEffect(() => {
    setFormReady(Object.values(formData.valid).every(v => v));
  }, [formData.valid]);
  useEffect(() => {
    if (formData.value) {
      formReady && props.onChange && props.onChange(formReady, formData.value, `${formData.firstName} ${formData.lastName}`);
    } else {
      formReady && props.onChange && props.onChange(formReady, noSignatureSvg(), `${formData.firstName} ${formData.lastName}`);
    }
  }, [formReady]);
  useEffect(() => {
    var _canvasRef$current2;
    (_canvasRef$current2 = canvasRef.current) === null || _canvasRef$current2 === void 0 ? void 0 : _canvasRef$current2.fromDataURL(formData.value || '');
  }, [formData.value]);
  const handleValidation = (fieldName, response) => {
    if (response === 'success') {
      setFormData({
        ...formData,
        valid: {
          ...formData.valid,
          [fieldName]: true
        }
      });
    } else {
      setFormData({
        ...formData,
        valid: {
          ...formData.valid,
          [fieldName]: false
        }
      });
    }
  };
  const getSignedName = () => {
    const middleName = (formData.middleName || '') !== '' ? formData.middleName + ' ' : '';
    return `${formData.firstName || ''} ${middleName}${formData.lastName || ''}`;
  };
  const reset = () => {
    var _canvasRef$current3, _canvasRef$current4;
    setPrevSignature('');
    setFormData({
      ...formData,
      value: ''
    });
    (_canvasRef$current3 = canvasRef.current) === null || _canvasRef$current3 === void 0 ? void 0 : _canvasRef$current3.on();
    (_canvasRef$current4 = canvasRef.current) === null || _canvasRef$current4 === void 0 ? void 0 : _canvasRef$current4.clear();
  };
  const handleSignatureCapture = () => {
    var _canvasRef$current5;
    setFormData({
      ...formData,
      value: ((_canvasRef$current5 = canvasRef.current) === null || _canvasRef$current5 === void 0 ? void 0 : _canvasRef$current5.toDataURL('image/svg+xml')) || ''
    });
  };
  const noSignatureSvg = () => {
    var _canvasRef$current6, _canvasRef$current7;
    const svg = /*#__PURE__*/React.createElement("svg", {
      className: "svg-no-signature-box svg-signature-text",
      xmlns: "http://www.w3.org/2000/svg",
      width: (_canvasRef$current6 = canvasRef.current) === null || _canvasRef$current6 === void 0 ? void 0 : _canvasRef$current6.clientWidth,
      height: (_canvasRef$current7 = canvasRef.current) === null || _canvasRef$current7 === void 0 ? void 0 : _canvasRef$current7.clientHeight,
      style: {
        backgroundColor: '#CFCFCF'
      }
    }, /*#__PURE__*/React.createElement("text", {
      x: "20",
      y: "35%",
      style: {
        fontSize: '2rem'
      }
    }, /*#__PURE__*/React.createElement("tspan", null, "Digital Signature Image Was Not Provided")), /*#__PURE__*/React.createElement("text", {
      x: "20",
      y: "75%",
      style: {
        fontWeight: 600
      }
    }, /*#__PURE__*/React.createElement("tspan", null, props.checkboxLabelText || CONSENT_TEXT)), /*#__PURE__*/React.createElement("text", {
      x: "20",
      y: "85%",
      style: {
        fontWeight: 600
      }
    }, /*#__PURE__*/React.createElement("tspan", null, "Full name as submitted: ", getSignedName())));
    return `data:image/svg+xml;base64,${btoa(renderToStaticMarkup(svg))}`;
  };
  return /*#__PURE__*/React.createElement(SignatureContainer, null, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(TextRow, null, /*#__PURE__*/React.createElement("div", {
    "aria-hidden": "true"
  }, "Sign here with mouse or finger"), /*#__PURE__*/React.createElement(ClearButton, {
    ghost: true,
    name: "clear signature",
    size: "small",
    type: "button",
    onClick: () => reset(),
    className: "secondary hollow",
    "aria-label": "Clear Signature"
  }, "Clear Signature")), /*#__PURE__*/React.createElement(SignatureCanvasDiv, null, /*#__PURE__*/React.createElement(SignatureCanvas, {
    onEnd: () => handleSignatureCapture(),
    ref: canvasRef
  }), /*#__PURE__*/React.createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: "96%",
    height: "18px"
  }, /*#__PURE__*/React.createElement("text", {
    x: "20",
    y: "15",
    style: {
      fill: '#555555',
      font: 'bold 15px sans-serif'
    }
  }, "X"), /*#__PURE__*/React.createElement("line", {
    x1: "35",
    y1: "15",
    x2: "100%",
    y2: "15",
    stroke: "#555555"
  }))), /*#__PURE__*/React.createElement(TextRow, null, /*#__PURE__*/React.createElement(ScreenReaderOnly, null, "Type full name"), /*#__PURE__*/React.createElement("div", {
    "aria-hidden": "true"
  }, "Type full name")), /*#__PURE__*/React.createElement(GridContainer, null, /*#__PURE__*/React.createElement(ComposedTextField, {
    name: "First Name",
    required: true,
    onChange: e => setFormData({
      ...formData,
      firstName: e
    }),
    onValidate: r => handleValidation('firstName', r)
  }), /*#__PURE__*/React.createElement(ComposedTextField, {
    name: "Middle Name",
    onChange: e => setFormData({
      ...formData,
      middleName: e
    }),
    onValidate: r => handleValidation('middleName', r)
  }), /*#__PURE__*/React.createElement(ComposedTextField, {
    name: "Last Name",
    required: true,
    onChange: e => setFormData({
      ...formData,
      lastName: e
    }),
    onValidate: r => handleValidation('lastName', r)
  }))), /*#__PURE__*/React.createElement(CheckBoxRow, null, /*#__PURE__*/React.createElement(Checkbox, {
    isRequired: true,
    onChange: selectedValue => {
      setFormData({
        ...formData,
        checked: selectedValue,
        valid: {
          ...formData.valid,
          checked: selectedValue
        }
      });
    }
  }, /*#__PURE__*/React.createElement("label", null, props.checkboxLabelText || CONSENT_TEXT))));
};
const FlexContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "Signature__FlexContainer",
  componentId: "adlo8v-0"
})(["display:flex;align-items:flex-end;justify-content:space-between;"]);
const TextRow = /*#__PURE__*/styled(FlexContainer).withConfig({
  displayName: "Signature__TextRow",
  componentId: "adlo8v-1"
})(["margin-left:5px;margin-bottom:10px;> div{margin-left:3px;}"]);
const CheckBoxRow = /*#__PURE__*/styled(FlexContainer).withConfig({
  displayName: "Signature__CheckBoxRow",
  componentId: "adlo8v-2"
})(["align-items:flex-start;justify-content:flex-start;margin:15px 0 0;label{font-weight:600;padding-left:0;}@media (min-width:769px){margin:15px 0 0 15px;label{padding-left:20px;}}"]);
const SignatureContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "Signature__SignatureContainer",
  componentId: "adlo8v-3"
})(["background:transparent;padding:5px;color:", ";"], emperor);
const SignatureCanvasDiv = /*#__PURE__*/styled.div.withConfig({
  displayName: "Signature__SignatureCanvasDiv",
  componentId: "adlo8v-4"
})(["margin-bottom:15px;border-radius:5px;background-color:", ";canvas{width:100%;}"], white);
const ScreenReaderOnly = /*#__PURE__*/styled.span.withConfig({
  displayName: "Signature__ScreenReaderOnly",
  componentId: "adlo8v-5"
})(["display:none;visibility:hidden;"]);
const ClearButton = /*#__PURE__*/styled(Button).withConfig({
  displayName: "Signature__ClearButton",
  componentId: "adlo8v-6"
})(["margin-right:0 !important;"]);
export default Signature;